/**
 * ====================================================================
 * FIKO CAD LEADER LINE RENDERER
 * ====================================================================
 *
 * Profesyonel leader line çizimi
 * Catmull-Rom spline, broken leaders, curve rendering
 *
 * @author FikoYT CAD Team
 * @version 2.0.0
 */

import * as THREE from 'three';
import { Line2 } from 'three/examples/jsm/lines/Line2.js';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import {
    LeaderLineConfig,
    ExtendedArrowStyle,
    LineStyleConfig,
    LineStyleType
} from '../visual/DimensionVisualStyle';
import { ArrowFactory } from './ArrowFactory';
import { getCanvasResolutionVec2 } from '../../../FikoCanvasResolution';

// ====================================================================
// TYPES
// ====================================================================

/**
 * Leader geometry result
 */
export interface LeaderGeometry {
    line: THREE.BufferGeometry | LineGeometry;
    arrow: THREE.BufferGeometry | null;
    arrowPosition: THREE.Vector3;
    arrowDirection: THREE.Vector3 | null;
    curvePoints: THREE.Vector3[];
}

/**
 * Leader line group (tüm bileşenler)
 */
export interface LeaderLineGroup {
    group: THREE.Group;
    line: THREE.Line | Line2;
    arrow: THREE.Mesh | null;
    breakPoints: THREE.Vector3[];
}

// ====================================================================
// LEADER LINE RENDERER
// ====================================================================

/**
 * Leader Line Renderer - Profesyonel leader çizimi
 */
export class LeaderLineRenderer {
    // Default configuration
    private static defaultConfig: LeaderLineConfig = {
        style: 'curved',
        tension: 0.4,
        segments: 32,
        breakDistance: 10,
        breakAngle: Math.PI / 5,
        arrowAtEnd: true,
        arrowAtStart: false,
        arrowStyle: ExtendedArrowStyle.FILLED,
        arrowSize: 0.7,
        lineStyle: {
            type: LineStyleType.SOLID,
            width: 1.5,
            opacity: 1
        }
    };

    /**
     * Catmull-Rom spline curve oluştur
     */
    static createSplineCurve(
        points: THREE.Vector3[],
        tension: number = 0.5,
        segments: number = 50
    ): THREE.CatmullRomCurve3 {
        const curve = new THREE.CatmullRomCurve3(
            points,
            false,  // closed
            'catmullrom',
            tension
        );

        return curve;
    }

    /**
     * Quadratic bezier curve oluştur
     */
    static createQuadraticCurve(
        start: THREE.Vector3,
        control: THREE.Vector3,
        end: THREE.Vector3,
        segments: number = 32
    ): THREE.QuadraticBezierCurve3 {
        return new THREE.QuadraticBezierCurve3(start, control, end);
    }

    /**
     * Cubic bezier curve oluştur
     */
    static createCubicCurve(
        start: THREE.Vector3,
        controlA: THREE.Vector3,
        controlB: THREE.Vector3,
        end: THREE.Vector3,
        segments: number = 32
    ): THREE.CubicBezierCurve3 {
        return new THREE.CubicBezierCurve3(start, controlA, controlB, end);
    }

    /**
     * Leader line geometrisi oluştur
     */
    static createLeaderGeometry(
        startPoint: THREE.Vector3,
        endPoint: THREE.Vector3,
        config?: Partial<LeaderLineConfig>
    ): LeaderGeometry {
        const cfg: LeaderLineConfig = { ...this.defaultConfig, ...config };

        let curvePoints: THREE.Vector3[];
        let lineGeometry: THREE.BufferGeometry | LineGeometry;

        switch (cfg.style) {
            case 'curved':
                curvePoints = this.createCurvedPath(startPoint, endPoint, cfg);
                break;
            case 'broken':
                curvePoints = this.createBrokenPath(startPoint, endPoint, cfg);
                break;
            case 'spline':
                curvePoints = this.createSplinePath(startPoint, endPoint, cfg);
                break;
            case 'straight':
            default:
                curvePoints = [startPoint.clone(), endPoint.clone()];
                break;
        }

        // LineGeometry (kalın çizgiler için)
        lineGeometry = new LineGeometry();
        const positions: number[] = [];
        curvePoints.forEach(p => positions.push(p.x, p.y, p.z));
        (lineGeometry as LineGeometry).setPositions(positions);

        // Ok geometrisi
        let arrowGeometry: THREE.BufferGeometry | null = null;
        let arrowDirection: THREE.Vector3 | null = null;
        let arrowPosition = endPoint.clone();

        if (cfg.arrowAtEnd && curvePoints.length >= 2) {
            // Son iki noktadan yön hesapla
            const lastPoint = curvePoints[curvePoints.length - 1];
            const prevPoint = curvePoints[curvePoints.length - 2];
            arrowDirection = new THREE.Vector3()
                .subVectors(lastPoint, prevPoint)
                .normalize();

            arrowGeometry = ArrowFactory.createArrow(
                cfg.arrowStyle,
                {
                    type: cfg.arrowStyle,
                    size: cfg.arrowSize,
                    aspectRatio: 2.5
                }
            );

            arrowPosition = lastPoint.clone();
        }

        return {
            line: lineGeometry,
            arrow: arrowGeometry,
            arrowPosition,
            arrowDirection,
            curvePoints
        };
    }

    /**
     * Curved path oluştur (single curve)
     */
    private static createCurvedPath(
        start: THREE.Vector3,
        end: THREE.Vector3,
        config: LeaderLineConfig
    ): THREE.Vector3[] {
        // Orta nokta için kontrol noktası
        const midPoint = new THREE.Vector3().lerpVectors(start, end, 0.5);

        // Perpendicular offset
        const direction = new THREE.Vector3().subVectors(end, start).normalize();
        const perpendicular = new THREE.Vector3(-direction.y, direction.x, 0);
        const offset = start.distanceTo(end) * 0.2;

        const controlPoint = midPoint.clone().add(perpendicular.multiplyScalar(offset));

        // Quadratic bezier curve
        const curve = new THREE.QuadraticBezierCurve3(start, controlPoint, end);
        return curve.getPoints(config.segments);
    }

    /**
     * Broken path oluştur (CATIA style)
     */
    private static createBrokenPath(
        start: THREE.Vector3,
        end: THREE.Vector3,
        config: LeaderLineConfig
    ): THREE.Vector3[] {
        const direction = new THREE.Vector3().subVectors(end, start).normalize();
        const distance = start.distanceTo(end);

        // İlk kırılma noktası
        const breakDist = Math.min(config.breakDistance, distance * 0.3);
        const breakPoint1 = start.clone().addScaledVector(direction, breakDist);

        // Kırılma açısı ile ikinci nokta
        const perpendicular = new THREE.Vector3(-direction.y, direction.x, 0);
        const lateralOffset = Math.tan(config.breakAngle) * breakDist;
        const breakPoint2 = breakPoint1.clone().add(perpendicular.multiplyScalar(lateralOffset));

        // End noktasına doğru son segment
        return [start.clone(), breakPoint1, breakPoint2, end.clone()];
    }

    /**
     * Spline path oluştur (smooth multi-point)
     */
    private static createSplinePath(
        start: THREE.Vector3,
        end: THREE.Vector3,
        config: LeaderLineConfig
    ): THREE.Vector3[] {
        const direction = new THREE.Vector3().subVectors(end, start).normalize();
        const distance = start.distanceTo(end);

        // Ara kontrol noktaları
        const point1 = start.clone().addScaledVector(direction, distance * 0.25);
        const point2 = start.clone().addScaledVector(direction, distance * 0.75);

        // Perpendicular offset
        const perpendicular = new THREE.Vector3(-direction.y, direction.x, 0);
        const offset = distance * 0.15;

        point1.add(perpendicular.clone().multiplyScalar(offset));
        point2.add(perpendicular.clone().multiplyScalar(-offset * 0.5));

        const curve = this.createSplineCurve(
            [start, point1, point2, end],
            config.tension,
            config.segments
        );

        return curve.getPoints(config.segments);
    }

    /**
     * Leader line grubu oluştur (render-ready)
     */
    static createLeaderLine(
        startPoint: THREE.Vector3,
        endPoint: THREE.Vector3,
        color: number = 0x000000,
        config?: Partial<LeaderLineConfig>
    ): LeaderLineGroup {
        const cfg: LeaderLineConfig = { ...this.defaultConfig, ...config };
        const geometry = this.createLeaderGeometry(startPoint, endPoint, cfg);

        const group = new THREE.Group();

        // Line material
        const lineMaterial = new LineMaterial({
            color,
            linewidth: cfg.lineStyle.width * 0.001,
            transparent: cfg.lineStyle.opacity < 1,
            opacity: cfg.lineStyle.opacity,
            depthTest: false,
            depthWrite: false,
            resolution: getCanvasResolutionVec2()
        });

        // Line2
        const line = new Line2(geometry.line as LineGeometry, lineMaterial);
        line.computeLineDistances();
        group.add(line);

        // Arrow
        let arrowMesh: THREE.Mesh | null = null;

        if (geometry.arrow && geometry.arrowDirection) {
            const arrowMaterial = new THREE.MeshBasicMaterial({
                color,
                transparent: cfg.lineStyle.opacity < 1,
                opacity: cfg.lineStyle.opacity,
                side: THREE.DoubleSide,
                depthTest: false,
                depthWrite: false
            });

            arrowMesh = new THREE.Mesh(geometry.arrow, arrowMaterial);
            arrowMesh.position.copy(geometry.arrowPosition);

            // Yön quaternion'u
            const quaternion = new THREE.Quaternion();
            quaternion.setFromUnitVectors(
                new THREE.Vector3(1, 0, 0),
                geometry.arrowDirection
            );
            arrowMesh.quaternion.copy(quaternion);

            group.add(arrowMesh);
        }

        // Render order
        group.renderOrder = 1000;

        return {
            group,
            line,
            arrow: arrowMesh,
            breakPoints: geometry.curvePoints
        };
    }

    /**
     * Diameter/Radius leader (annotation arrow)
     */
    static createDimensionLeader(
        surfacePoint: THREE.Vector3,
        textPosition: THREE.Vector3,
        color: number = 0x000000,
        config?: Partial<LeaderLineConfig>
    ): LeaderLineGroup {
        const cfg: LeaderLineConfig = {
            ...this.defaultConfig,
            style: 'curved',
            arrowAtEnd: false,
            arrowAtStart: true, // Ok geometri tarafında
            ...config
        };

        // Start ve end'i ters çevir (ok başlangıçta)
        return this.createLeaderLine(textPosition, surfacePoint, color, cfg);
    }

    /**
     * Multi-segment leader (multiple break points)
     */
    static createMultiSegmentLeader(
        points: THREE.Vector3[],
        color: number = 0x000000,
        config?: Partial<LeaderLineConfig>
    ): LeaderLineGroup {
        const cfg: LeaderLineConfig = { ...this.defaultConfig, ...config };

        const group = new THREE.Group();

        let curvePoints: THREE.Vector3[];

        if (cfg.style === 'spline' && points.length >= 3) {
            // Spline curve tüm noktalardan geçer
            const curve = this.createSplineCurve(points, cfg.tension, cfg.segments);
            curvePoints = curve.getPoints(cfg.segments);
        } else {
            // Düz çizgi segmentleri
            curvePoints = [...points];
        }

        // LineGeometry
        const lineGeometry = new LineGeometry();
        const positions: number[] = [];
        curvePoints.forEach(p => positions.push(p.x, p.y, p.z));
        lineGeometry.setPositions(positions);

        // Line material
        const lineMaterial = new LineMaterial({
            color,
            linewidth: cfg.lineStyle.width * 0.001,
            transparent: cfg.lineStyle.opacity < 1,
            opacity: cfg.lineStyle.opacity,
            depthTest: false,
            depthWrite: false,
            resolution: getCanvasResolutionVec2()
        });

        const line = new Line2(lineGeometry, lineMaterial);
        line.computeLineDistances();
        group.add(line);

        // Arrow at end
        let arrowMesh: THREE.Mesh | null = null;

        if (cfg.arrowAtEnd && curvePoints.length >= 2) {
            const lastPoint = curvePoints[curvePoints.length - 1];
            const prevPoint = curvePoints[curvePoints.length - 2];
            const arrowDirection = new THREE.Vector3()
                .subVectors(lastPoint, prevPoint)
                .normalize();

            const arrowGeometry = ArrowFactory.createArrow(
                cfg.arrowStyle,
                { type: cfg.arrowStyle, size: cfg.arrowSize, aspectRatio: 2.5 }
            );

            const arrowMaterial = new THREE.MeshBasicMaterial({
                color,
                side: THREE.DoubleSide,
                depthTest: false,
                depthWrite: false
            });

            arrowMesh = new THREE.Mesh(arrowGeometry, arrowMaterial);
            arrowMesh.position.copy(lastPoint);

            const quaternion = new THREE.Quaternion();
            quaternion.setFromUnitVectors(new THREE.Vector3(1, 0, 0), arrowDirection);
            arrowMesh.quaternion.copy(quaternion);

            group.add(arrowMesh);
        }

        group.renderOrder = 1000;

        return {
            group,
            line,
            arrow: arrowMesh,
            breakPoints: curvePoints
        };
    }

    /**
     * Balloon leader (daire ile)
     */
    static createBalloonLeader(
        attachPoint: THREE.Vector3,
        balloonCenter: THREE.Vector3,
        balloonRadius: number,
        color: number = 0x000000,
        config?: Partial<LeaderLineConfig>
    ): { leader: LeaderLineGroup; balloon: THREE.Mesh } {
        const cfg: LeaderLineConfig = { ...this.defaultConfig, style: 'curved', ...config };

        // Leader line (balloon'un kenarına)
        const direction = new THREE.Vector3().subVectors(attachPoint, balloonCenter).normalize();
        const balloonEdge = balloonCenter.clone().add(direction.multiplyScalar(balloonRadius));

        const leader = this.createLeaderLine(balloonEdge, attachPoint, color, cfg);

        // Balloon (daire)
        const balloonGeometry = new THREE.CircleGeometry(balloonRadius, 32);
        const balloonMaterial = new THREE.MeshBasicMaterial({
            color: 0xFFFFFF,
            side: THREE.DoubleSide,
            transparent: true,
            opacity: 0.9,
            depthTest: false,
            depthWrite: false
        });

        const balloon = new THREE.Mesh(balloonGeometry, balloonMaterial);
        balloon.position.copy(balloonCenter);

        // Balloon outline
        const outlineGeometry = new THREE.RingGeometry(balloonRadius - 0.05, balloonRadius, 32);
        const outlineMaterial = new THREE.MeshBasicMaterial({
            color,
            side: THREE.DoubleSide,
            depthTest: false,
            depthWrite: false
        });

        const outline = new THREE.Mesh(outlineGeometry, outlineMaterial);
        outline.position.copy(balloonCenter);
        outline.position.z += 0.01;

        leader.group.add(balloon, outline);

        return { leader, balloon };
    }

    /**
     * Resolution güncelle
     */
    static updateResolution(width: number, height: number): void {
        // LineMaterial'ların resolution'ını güncelle
        // Bu fonksiyon external olarak çağrılmalı
    }
}

// ====================================================================
// EXPORTS
// ====================================================================

export type { LeaderLineConfig };
export { ExtendedArrowStyle };
